<script setup lang="ts">
import { computed, inject, onBeforeUnmount, type Component } from 'vue';
import { CheckmarkIcon } from '@blpde/nx-icons';
import { stepperContextKey } from './internal';
import type { StepStatus } from './useStepperRegistry';

const props = defineProps<{
  /** Text label shown under the marker. The default slot overrides it. */
  label?: string;
  /** Custom marker icon; replaces the step number / checkmark. */
  icon?: Component;
  /** Force a status instead of deriving it from the active index. */
  status?: StepStatus;
}>();

const registry = inject(stepperContextKey);
if (!registry) throw new Error('[nx-ui] NxStep must be used inside NxStepper');

const id = Symbol('nx-step');
onBeforeUnmount(registry.register(id));

const index = registry.indexOf(id);
const status = computed<StepStatus>(() => props.status ?? registry.statusFor(index.value));
const interactive = computed(() => registry.canNavigate(index.value));

const onActivate = (): void => {
  registry.goTo(index.value);
};
</script>

<template>
  <li
    class="nx-step"
    :class="`is-${status}`"
    :aria-current="status === 'active' ? 'step' : undefined"
  >
    <component
      :is="interactive ? 'button' : 'div'"
      :type="interactive ? 'button' : undefined"
      class="nx-step-control"
      :class="{ 'is-interactive': interactive }"
      @click="interactive && onActivate()"
    >
      <span class="nx-step-marker">
        <component :is="icon" v-if="icon" class="nxt-icon-md" />
        <CheckmarkIcon v-else-if="status === 'done'" filled class="nxt-icon-md" />
        <span v-else class="nx-step-number">{{ index + 1 }}</span>
      </span>
      <span class="nx-step-label">
        <slot>{{ label }}</slot>
      </span>
    </component>
  </li>
</template>

<style scoped>
/* NxStep consumes the --nxt-step-* component tokens (I-596): default marker
   reads step-bg/step-fg, the active marker re-points to step-bg-current/
   step-fg-current (solid brand, plus a soft halo so it stands apart), and the
   done marker is solid brand with the check in step-fg-current — a finished
   step must read as achieved, not as greyed out (the soft step-bg-done fill was
   paler than the open steps). The connector reads --nxt-step-connector by
   default and switches to --nxt-brand-bg once done. */
.nx-step {
  /* rem (not em) so marker geometry and connector math share one reference,
     independent of the size-driven font scale. Fallbacks match size 'm'. */
  --nxt-step-marker-size: var(--nxt-stepper-marker-size, calc(var(--nxt-size-unit) * 2));
  --nxt-step-font-size: var(--nxt-stepper-font-size, var(--nxt-font-size-sm));
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
}

/* Connector line to the next step, centered on the marker row. */
.nx-step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: calc(var(--nxt-step-marker-size) / 2);
  left: calc(50% + var(--nxt-step-marker-size) / 2);
  width: calc(100% - var(--nxt-step-marker-size));
  height: calc(var(--nxt-size-unit) * 0.125);
  transform: translateY(-50%);
  background: var(--nxt-step-connector);
  z-index: 0;
}

.nx-step.is-done::after {
  background: var(--nxt-brand-bg);
}

.nx-step-control {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: center;
}

.nx-step-control.is-interactive {
  cursor: pointer;
}

.nx-step-marker {
  position: relative;
  z-index: var(--nxt-z-raised);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-step-marker-size);
  height: var(--nxt-step-marker-size);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-border);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
  color: var(--nxt-step-fg);
  font-size: var(--nxt-step-font-size);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-step-control.is-interactive:hover .nx-step-marker {
  border-color: var(--nxt-border-strong);
}

.nx-step.is-active .nx-step-marker {
  background: var(--nxt-step-bg-current);
  border-color: var(--nxt-step-bg-current);
  color: var(--nxt-step-fg-current);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 4)
    color-mix(in srgb, var(--nxt-step-bg-current) 20%, transparent);
}

.nx-step.is-done .nx-step-marker {
  background: var(--nxt-step-bg-current);
  border-color: var(--nxt-step-bg-current);
  color: var(--nxt-step-fg-current);
}

.nx-step.is-done .nx-step-control.is-interactive:hover .nx-step-marker {
  background: var(--nxt-brand-bg-hover);
  border-color: var(--nxt-brand-bg-hover);
}

.nx-step-control:focus-visible {
  outline: none;
}

.nx-step-control:focus-visible .nx-step-marker {
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg),
    0 0 0 calc(var(--nxt-size-unit) * 0.25) var(--nxt-focus-ring);
}

.nx-step-label {
  font-size: var(--nxt-step-font-size);
  color: var(--nxt-fg-muted);
}

.nx-step.is-active .nx-step-label {
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-step.is-done .nx-step-label {
  color: var(--nxt-fg);
}

.nx-step-control.is-interactive:hover .nx-step-label {
  color: var(--nxt-fg);
  text-decoration: underline;
  text-decoration-color: var(--nxt-border-strong);
  text-underline-offset: calc(var(--nxt-hairline) * 3);
}

@media (prefers-reduced-motion: reduce) {
  .nx-step-marker {
    transition: none;
  }
}
</style>
